/* eslint-disable max-lines */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import { useStoreon } from 'storeon/react';
import {
  List,
  Section,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
  Swiper,
  SwiperSlide,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { COOKIES_STORE_NAME, getLang } from 'modules/cookies';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { WINDOWS_CDN_PATH, WINDOWS_ELEMENT_BLOCK } from '../../windows-constants';
import { BrandName } from '../../windows-types';
import {
  REHAU_AND_DECEUNINCK_BRAND_REGION_LIST,
  WINDOWS_BRANDS_SELECTION_GROUP_OPTION_LIST,
} from './windows-brands-section-constants';
import {
  WindowsBrandExample,
  windowsBrandsAdditionalInfo,
  windowsBrandsAdditionalInfoList,
  windowsBrandsLogo,
  windowsBrandsRehauSlide,
  windowsBrandsSelectionGroup,
  windowsBrandsSlide,
  windowsBrandsSlideImage,
  windowsBrandsSlideName,
  windowsBrandsSlideProperties,
  windowsBrandsSwiper,
  windowsBrandsTextContainer,
  windowsBrandsTextWrapper,
  windowsBrandsTitle,
} from './windows-brands-section-styles';
import { getBrandConfig } from './windows-brands-section-utils';

type WindowsBrandsSectionProps = {
  brandName: BrandName;
  onChangeBrand: (brandName: BrandName) => void;
  regionId: string;
};

export const WindowsBrandsSection = ({ brandName, onChangeBrand, regionId }: WindowsBrandsSectionProps) => {
  const { t } = useTranslation();
  const { cookies } = useStoreon(COOKIES_STORE_NAME);
  const lang = getLang(cookies);
  const bannerRefs = useRef<HTMLDivElement[]>([]);
  const [activeTab, setActiveTab] = useState(brandName);

  const config = useMemo(() => getBrandConfig(brandName), [brandName]);
  const slideStyles = useMemo(
    () => cn(windowsBrandsSlide, { [windowsBrandsRehauSlide]: brandName === 'rehau' }),
    [brandName]
  );

  const isSelectionGroupVisible = useMemo(() => REHAU_AND_DECEUNINCK_BRAND_REGION_LIST.includes(regionId), [
    regionId,
  ]);

  const promotions = useMemo(() => {
    if (!config) {
      return [];
    }
    return config.detailedInfo.map(({ name }, index: number) => ({
      name: `${WINDOWS_ELEMENT_BLOCK} / ${brandName}`,
      creative: name,
      position: (index + 1).toString(),
    }));
  }, [brandName, config]);

  usePromoViewEvent(bannerRefs, `${WINDOWS_ELEMENT_BLOCK} / ${brandName}`, promotions);

  const handleTabChange = useCallback(
    /* @ts-expect-error: param typecheck error */
    (_, value: SelectionGroupValue) => {
      setActiveTab(value as BrandName);
      onChangeBrand(value as BrandName);

      const activeOption: SelectionGroupOption | undefined = WINDOWS_BRANDS_SELECTION_GROUP_OPTION_LIST.find(
        (option: SelectionGroupOption) => option.value === value
      );
      if (activeOption) {
        analyticsUtils.clickButtonEvent(WINDOWS_ELEMENT_BLOCK, activeOption.text, 'Бренд окон');
      }
    },
    [onChangeBrand]
  );

  const handleBannerClick = useCallback(
    (index: number) => () => {
      analyticsUtils.promoClickEvent(`${WINDOWS_ELEMENT_BLOCK} / ${brandName}`, promotions[index]);
    },
    [brandName, promotions]
  );

  if (!config) {
    return null;
  }

  return (
    <Section>
      {isSelectionGroupVisible && (
        <SelectionGroup
          active={activeTab}
          className={windowsBrandsSelectionGroup}
          onChange={handleTabChange}
          optionList={WINDOWS_BRANDS_SELECTION_GROUP_OPTION_LIST}
          rounded
        />
      )}
      <div className={windowsBrandsTextContainer}>
        <div className={windowsBrandsTextWrapper}>
          <SectionSubTitle className={windowsBrandsTitle} margin="s">
            {t(config.title)}
          </SectionSubTitle>
          <Typography variant="m" size="body_long">
            {t(config.description)}
          </Typography>
        </div>
        <img
          className={windowsBrandsLogo}
          src={`${WINDOWS_CDN_PATH}/brands/${brandName}.png/${brandName}.png`}
          alt={t(config.title) || ''}
        />
      </div>
      <Swiper
        breakpoints={{
          320: { enabled: true },
          728: { enabled: false },
        }}
        className={windowsBrandsSwiper}
        slidesPerView="auto"
        watchOverflow>
        {config.detailedInfo.map(
          (
            { imgSrc, name, mountingWidth, numberOfCameras, doubleGlazing, thermalInsulation },
            index: number
          ) => (
            <SwiperSlide className={slideStyles} key={createElementKey('brand', index)}>
              <WindowsBrandExample
                data-position={index}
                onClick={handleBannerClick(index)}
                ref={(el: HTMLDivElement | null) => {
                  if (el && bannerRefs) bannerRefs.current[index] = el;
                }}>
                <img alt={name} title={name} className={windowsBrandsSlideImage} src={imgSrc} />
                <Typography adaptive={false} className={windowsBrandsSlideName} variant="m" size="subtitle">
                  {name}
                </Typography>
                <Typography
                  className={windowsBrandsSlideProperties}
                  color="text-secondary"
                  variant="s"
                  size="body_long">
                  {t('brands-section.property-first')}:{' '}
                  <Typography as="span" variant="s" size="body_long">
                    {mountingWidth}
                  </Typography>
                </Typography>
                <Typography
                  className={windowsBrandsSlideProperties}
                  color="text-secondary"
                  variant="s"
                  size="body_long">
                  {t('brands-section.property-second')}:{' '}
                  <Typography as="span" variant="s" size="body_long">
                    {numberOfCameras}
                  </Typography>
                </Typography>
                <Typography
                  className={windowsBrandsSlideProperties}
                  color="text-secondary"
                  variant="s"
                  size="body_long">
                  {t('brands-section.property-third')}:{' '}
                  <Typography as="span" variant="s" size="body_long">
                    {thermalInsulation}
                  </Typography>
                </Typography>
                <Typography
                  className={windowsBrandsSlideProperties}
                  color="text-secondary"
                  variant="s"
                  size="body_long">
                  {t('brands-section.property-fourth')}:{' '}
                  <Typography as="span" variant="s" size="body_long">
                    {doubleGlazing}
                  </Typography>
                </Typography>
              </WindowsBrandExample>
            </SwiperSlide>
          )
        )}
      </Swiper>

      <div className={windowsBrandsAdditionalInfo}>
        <List className={windowsBrandsAdditionalInfoList}>
          <li>
            <Typography as="span" variant="caption">
              <Typography as="span" variant="caption_accent">
                {t('brands-section.add-info-first1')}
              </Typography>{' '}
              {t('brands-section.add-info-first2')}
            </Typography>
          </li>
          <li>
            <Typography as="span" variant="caption">
              <Typography as="span" variant="caption_accent">
                {t('brands-section.add-info-second1')}
              </Typography>{' '}
              {t('brands-section.add-info-second2')}
            </Typography>
          </li>
          <li>
            <Typography as="span" variant="caption">
              {lang === 'ru' && (
                <>
                  <Typography as="span" variant="caption_accent">
                    {t('brands-section.add-info-third1')}
                  </Typography>{' '}
                </>
              )}
              {t('brands-section.add-info-third2')}
            </Typography>
          </li>
        </List>
      </div>
    </Section>
  );
};
